import { Platform, View, ViewStyle, StyleSheet } from 'react-native';
import React, { createElement } from 'react';

function getDataTransfer(e: unknown): DataTransfer | null {
  const ev = e as { dataTransfer?: DataTransfer; nativeEvent?: { dataTransfer?: DataTransfer } };
  return ev?.dataTransfer ?? ev?.nativeEvent?.dataTransfer ?? null;
}

const MIME = 'application/x-ology-widget-index';

type DivDragProps = {
  draggable: boolean;
  onDragStart: (e: React.DragEvent<HTMLDivElement>) => void;
  onDragOver: (e: React.DragEvent<HTMLDivElement>) => void;
  onDrop: (e: React.DragEvent<HTMLDivElement>) => void;
};

/**
 * Web: HTML5 drag-and-drop on a real div (RN View often does not receive draggable reliably).
 * Native: plain View wrapper.
 */
export default function DraggableOrderChip({
  index,
  disabled,
  onReorder,
  children,
  style,
}: {
  index: number;
  disabled?: boolean;
  onReorder: (fromIndex: number, toIndex: number) => void;
  children: React.ReactNode;
  style?: ViewStyle | ViewStyle[];
}): JSX.Element {
  if (Platform.OS !== 'web') {
    return <View style={style}>{children}</View>;
  }

  const flat = StyleSheet.flatten(style) as Record<string, unknown> | undefined;
  const divStyle: React.CSSProperties = {
    display: 'inline-flex',
    flexDirection: 'column',
    alignItems: 'center',
    verticalAlign: 'top',
    cursor: disabled ? 'default' : 'grab',
    touchAction: 'none',
    userSelect: 'none',
    ...(flat as React.CSSProperties),
  };

  const dragProps: DivDragProps = {
    draggable: !disabled,
    onDragStart: (e) => {
      if (disabled) return;
      e.dataTransfer.setData(MIME, String(index));
      e.dataTransfer.effectAllowed = 'move';
    },
    onDragOver: (e) => {
      if (disabled) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = 'move';
    },
    onDrop: (e) => {
      if (disabled) return;
      e.preventDefault();
      const raw = e.dataTransfer.getData(MIME);
      if (raw == null || raw === '') return;
      const from = parseInt(raw, 10);
      if (Number.isNaN(from) || from === index) return;
      onReorder(from, index);
    },
  };

  return createElement(
    'div',
    { style: divStyle, ...dragProps } as React.HTMLAttributes<HTMLDivElement> & DivDragProps,
    children
  ) as unknown as JSX.Element;
}
